import React, { useState, useEffect, useCallback, lazy, Suspense } from 'react';
import './App.css';
// App: Root application shell. Defines routes and passes top-level props.
// Routing is symmetrical via Route components in `src/pages/*`.
import Header from './components/Header';

import CollectionRoute from './pages/CollectionRoute';
import DeckListRoute from './pages/DeckListRoute';
import PlaytestLobbyRoute from './features/playtest/routes/PlaytestLobbyRoute';
import PlaytestBoardRoute from './features/playtest/routes/PlaytestBoardRoute';
import JoinPlaytestPage from './features/playtest/routes/JoinPlaytestPage';
import DeckEditorRoute from './features/deckEditor/DeckEditorRoute';
import DeckCatalogPage from './pages/DeckCatalogPage';
import WantlistPage from './pages/WantlistPage';
import FriendsPage from './components/FriendsPage';
import AuthPage from './components/AuthPage';
import PlaytestEntryPage from './features/playtest/routes/PlaytestEntryPage';
import ImpressumPage from './pages/ImpressumPage';
import ResetPasswordPage from './pages/ResetPasswordPage';
import DatenschutzPage from './pages/DatenschutzPage';
import KontaktPage from './pages/KontaktPage';
import { Routes, Route, Navigate, useNavigate, useLocation } from 'react-router-dom';
import ProtectedRoute from './routes/ProtectedRoute';
import AdminRoute from './routes/AdminRoute';
import { useAuth } from './context/AuthContext';
import { useTheme } from './context/ThemeContext';
import { PlaytestProvider } from './context/PlaytestContext';
import { ProfileProvider } from './context/ProfileContext';
import { queryClient } from './queryClient';
import AdminDashboardRoute from './pages/AdminDashboardRoute';
import { apiClient, fetchWithAuth, json } from './api/apiClient';
import { reportError } from './api/errorReporting';
import ErrorBoundary from './ui/ErrorBoundary';
import { TooltipProvider } from './ui/Tooltip';

// Lazy-load Komponenten die react-markdown benötigen (werden erst beim ersten Rendern geladen)
const PatchNotesPopup = lazy(() => import('./components/PatchNotesPopup'));
const PatchNotesAdminRoute = lazy(() => import('./pages/PatchNotesAdminRoute'));
const DesignSystemPage = lazy(() => import('./design/DesignSystemPage'));

function App() {
  // --- Authentication State via Context ---
  const { isAuthenticated, currentUser, authChecking, login, logout } = useAuth();
  const { loadThemeFromPreferences } = useTheme();
  const navigate = useNavigate();
  const location = useLocation();

  // --- Global App State ---
  // Verwenden des Routers für Seiten; kein lokaler currentPage
  const [loading, setLoading] = useState(false);
  const [unreadPatchNotes, setUnreadPatchNotes] = useState([]);
  const [showPatchNotes, setShowPatchNotes] = useState(false);

  // Ungelesene Patch Notes nach Login laden
  useEffect(() => {
    if (!isAuthenticated) return;
    const loadUnread = async () => {
      try {
        const resp = await fetchWithAuth('/api/patch-notes/unread');
        if (resp.ok) {
          const data = await resp.json();
          if (data.patchNotes && data.patchNotes.length > 0) {
            setUnreadPatchNotes(data.patchNotes);
            setShowPatchNotes(true);
          }
        }
      } catch (e) {
        // Stille Fehlerbehandlung
      }
    };
    loadUnread();
  }, [isAuthenticated]);

  const handleDisablePatchNotes = useCallback(async () => {
    try {
      await fetchWithAuth('/api/me/preferences', {
        method: 'PATCH',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ patchNotesDisabled: true }),
      });
    } catch (e) {
      // Stille Fehlerbehandlung
    }
  }, []);

  const handleLoginSuccess = (user, token) => {
    login(user, token);
    navigate('/collection', { replace: true });
    // Theme aus gespeicherten Preferences laden
    loadThemeFromPreferences();
  };

  const handleLogout = () => { logout(); };

  // --- Pending Drive-Import nach OAuth-Redirect automatisch fortsetzen ---
  React.useEffect(() => {
    if (!isAuthenticated) return;
    const pendingId = sessionStorage.getItem('pendingDriveFileId');
    if (pendingId) {
      sessionStorage.removeItem('pendingDriveFileId');
      // Kurze Verzögerung damit die Seite vollständig geladen ist
      setTimeout(() => handleLoadFromDrive(pendingId), 500);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isAuthenticated]);

  // --- API Funktionen für die Sammlung ---
  const handleLoadFromDrive = async (fileId = '') => {
    setLoading(true);

    try {
      await apiClient.post('/api/load-from-drive', { fileId });
      // Trigger collection refresh
      queryClient.invalidateQueries({ queryKey: ['cards'] });
    } catch (err) {
      // Fehlende Drive-Freigabe: der Server liefert die Google-Zustimmungs-Adresse mit;
      // die Datei-ID überdauert den Umweg über Google in der sessionStorage.
      if (err.code === 'DRIVE_AUTH_REQUIRED' && err.details?.authUrl) {
        sessionStorage.setItem('pendingDriveFileId', fileId);
        window.location.href = err.details.authUrl;
        return;
      }
      if (err.status === 401) {
        logout();
        navigate('/auth', { replace: true });
        return;
      }
      console.error('Fehler beim Laden der Sammlung:', err);
    } finally {
      setLoading(false);
    }
  };

  const handleUploadCsv = async (file) => {
    setLoading(true);
    try {
      const formData = new FormData();
      formData.append('file', file);

      // FormData setzt seinen Content-Type mit Boundary selbst — deshalb json() statt apiClient.post
      await json('/api/upload-csv', { method: 'POST', body: formData });
      // Trigger collection refresh
      queryClient.invalidateQueries({ queryKey: ['cards'] });
    } catch (err) {
      if (err.status === 401) {
        logout();
        navigate('/auth', { replace: true });
        return;
      }
      console.error('Fehler beim CSV-Upload:', err);
    } finally {
      setLoading(false);
    }
  };


  // Jede Route bekommt ihr eigenes Auffangnetz: ein Render-Fehler kostet den Seiteninhalt,
  // Header und Navigation daneben bleiben bedienbar. Der Pfad als resetKey verwirft den
  // Fehlerzustand beim Navigieren, sonst bliebe die Fallback-Karte auf der nächsten Seite stehen.
  const guard = (element) => (
    <ErrorBoundary onError={reportError} resetKey={location.pathname}>{element}</ErrorBoundary>
  );

  return (
    <div className="App">
      <TooltipProvider>
        <Routes>
          {/* Public routes — accessible without login */}
          <Route path="/impressum" element={<ImpressumPage />} />
          <Route path="/datenschutz" element={<DatenschutzPage />} />
          <Route path="/kontakt" element={<KontaktPage />} />
          <Route path="/reset-password" element={<ResetPasswordPage onLoginSuccess={handleLoginSuccess} />} />

          {/* All other routes — auth gate */}
          <Route path="*" element={
            authChecking ? (
              <div style={{ padding: 40, textAlign: 'center', color: 'var(--text-muted)' }}>Laden...</div>
            ) : !isAuthenticated ? (
              <AuthPage onLoginSuccess={handleLoginSuccess} />
            ) : (
              <>
                <Header
                  currentUser={currentUser}
                  onLogout={handleLogout}
                  onReloadCollection={handleLoadFromDrive}
                  onUploadCsv={handleUploadCsv}
                  isLoading={loading}
                />

                <ProfileProvider>
                  <PlaytestProvider>
                    {showPatchNotes && unreadPatchNotes.length > 0 && (
                      <Suspense fallback={null}>
                        <PatchNotesPopup
                          patchNotes={unreadPatchNotes}
                          onClose={() => setShowPatchNotes(false)}
                          onDisable={handleDisablePatchNotes}
                        />
                      </Suspense>
                    )}
                    <Routes>
                      <Route element={<ProtectedRoute />}>
                        <Route path="/collection" element={guard(<CollectionRoute />)} />
                        <Route path="/decks" element={guard(<DeckListRoute />)} />
                        <Route path="/decks/:deckId" element={<Navigate to="editor" replace />} />
                        <Route path="/decks/:deckId/editor" element={guard(<DeckEditorRoute />)} />
                        <Route path="/decks/:deckId/katalog" element={guard(<DeckCatalogPage />)} />
                        <Route path="/friends" element={guard(<FriendsPage />)} />
                        <Route path="/wantlist" element={guard(<WantlistPage />)} />

                        {/* Playtest Routes */}
                        <Route path="/playtest" element={guard(<PlaytestEntryPage />)} />
                        <Route path="/playtest/join/:code" element={guard(<JoinPlaytestPage />)} />
                        <Route path="/playtest/lobby/:sessionId" element={guard(<PlaytestLobbyRoute />)} />
                        <Route path="/playtest/board/:sessionId" element={guard(<PlaytestBoardRoute />)} />

                        <Route path="/design" element={
                          <Suspense fallback={<div style={{ padding: 40, textAlign: 'center', color: 'var(--text-muted)' }}>Laden...</div>}>
                            <DesignSystemPage />
                          </Suspense>
                        } />
                        <Route path="/" element={<Navigate to="/collection" replace />} />
                      </Route>

                      {/* Admin Route (nur für Admins) */}
                      <Route element={<AdminRoute />}>
                        <Route path="/admin" element={guard(<AdminDashboardRoute />)} />
                        <Route path="/admin/patch-notes" element={guard(
                          <Suspense fallback={<div style={{ padding: 40, textAlign: 'center', color: 'var(--text-muted)' }}>Laden...</div>}>
                            <PatchNotesAdminRoute />
                          </Suspense>
                        )} />
                      </Route>
                    </Routes>
                  </PlaytestProvider>
                </ProfileProvider>
              </>
            )
          } />
        </Routes>
      </TooltipProvider>
    </div>
  );
}

export default App;
